import { Pressable } from '@react-aria/interactions'
import { Item } from '@react-stately/collections'
import { AssigneeType, DefaultType, PostStatusPropertyValue, PropertyValue } from 'api/clients/PropertyClient'
import { Tag } from 'common/OptionSelect/Tag'
import { usePostProperties } from 'components/ArticlePostPreview/usePostProperties'
import { useIsChannelPostable } from 'components/PostStatusDropdown/utils'
import { Select } from 'components/Select/Select'
import { useProperties } from 'hooks/useProperties'
import { useRootStore } from 'stores/RootStore'
import { AlmanacFile, AlmanacFileSummary } from 'types'
import Styles from './TopBar.module.scss'
import { useIsScheduledPostsV3 } from 'hooks/isScheduledPostsV3'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'
import {
  STATUS_COLOR_V2,
  STATUS_FONT_COLOR_V2,
} from 'screens/ScheduledPosts/MobileCalendar/components/CalendarItem/CalendarItem'

interface StatusButtonProps {
  file: AlmanacFileSummary | AlmanacFile
}

export const StatusButton = ({ file }: StatusButtonProps) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const { designStore } = useRootStore()
  const { status, updateStatus } = usePostProperties({ id: file?.id as string, type: AssigneeType.Design })
  const statusColor = (status as PropertyValue)?.meta?.color as string
  const { currentWorkspaceProperties } = useProperties()
  const { isChannelPostable } = useIsChannelPostable()
  const isScheduledPostsV3 = useIsScheduledPostsV3()
  const postStatusProperty = currentWorkspaceProperties.find((p) => p.default_type === DefaultType.PostStatus)
  const nonPostablePropertyValues = postStatusProperty?.property_values.filter((propertyValue) =>
    ['Posted', 'Draft'].includes(propertyValue.value),
  )

  if (!file.channel) {
    return null
  }

  if (isScheduledPostsV3 && isChannelPostable(file.channel) && status) {
    return (
      <div className={Styles.statusButtonContainer} aria-label="status">
        <Tag
          fontColor={isSystemRedesignEnabled ? STATUS_FONT_COLOR_V2[status.value] : undefined}
          color={isSystemRedesignEnabled ? STATUS_COLOR_V2[status.value] : statusColor}
        >
          {status.value}
        </Tag>
      </div>
    )
  }

  if (isChannelPostable(file.channel) && status) {
    return (
      <Pressable onPress={() => designStore?.openTab('publish')}>
        <div className={Styles.statusButtonContainer} role="button" aria-label="status">
          <Tag color={statusColor}>{status.value}</Tag>
        </div>
      </Pressable>
    )
  }

  if (!nonPostablePropertyValues) {
    return null
  }

  return (
    <div className={Styles.statusSelectContainer}>
      <Select
        selectedKey={status.value}
        onSelectionChange={(key) => updateStatus(key as PostStatusPropertyValue)}
        endIcon={() => <></>}
        aria-label="Status selector"
      >
        {nonPostablePropertyValues?.map((propertyValue) => (
          <Item key={propertyValue.value} textValue={propertyValue.value}>
            <Tag color={propertyValue.meta?.color || ''}>{propertyValue.value}</Tag>
          </Item>
        ))}
      </Select>
    </div>
  )
}
